import { ActionTree, GetterTree, MutationTree } from "vuex";
import { AssistWriterType } from "@/lib/models/assist";
import { AssistantCatClient } from "@/lib/clients/assistantCatClient";
import {
  AssistantCatMaster,
  AssistantCat,
  UpdateAssistantCatMasterInput,
  UpdateAssistantCatInput,
} from "@/lib/models/assistantCat";

const assistantCatClient = new AssistantCatClient();

export interface AssistantCatState {
  selectedType: AssistWriterType | null;
  serializedResult: string | null;
  assistantCatMaster: AssistantCatMaster | null;
  assistantCats: AssistantCat[];
  dryModeExtraFish: number;
}

const getters: GetterTree<AssistantCatState, {}> = {
  selectedType: (state) => state.selectedType,
  serializedResult: (state) => state.serializedResult,
  assistantCatMaster: (state) => state.assistantCatMaster,
  assistantCats: (state) => state.assistantCats,
  dryModeExtraFish: (state) => state.dryModeExtraFish,
};

const mutations: MutationTree<AssistantCatState> = {
  setSelectedType(state, selectedType: AssistWriterType | null) {
    state.selectedType = selectedType;
  },
  setSerializedResult(state, serializedResult: string | null) {
    state.serializedResult = serializedResult;
  },
  setAssistantCatMaster(state, assistantCatMaster: AssistantCatMaster | null) {
    state.assistantCatMaster = assistantCatMaster;
    if (assistantCatMaster) {
      state.selectedType = assistantCatMaster.selectedType;
      state.serializedResult = assistantCatMaster.serializedResult || null;
    }
  },
  setAssistantCats(state, assistantCats: AssistantCat[]) {
    state.assistantCats = assistantCats;
  },
  setDryModeExtraFish(state, value: number) {
    state.dryModeExtraFish = value;
  },
  updateAssistantCatInList(state, updatedCat: AssistantCat) {
    const index = state.assistantCats.findIndex((cat) => cat.assistantCatType === updatedCat.assistantCatType);
    if (index !== -1) {
      state.assistantCats.splice(index, 1, updatedCat);
    }
  },
};

const actions: ActionTree<AssistantCatState, {}> = {
  setSelectedCat({ commit }, { selectedType, serializedResult = null }) {
    commit("setSelectedType", selectedType);
    commit("setSerializedResult", serializedResult);
  },
  async fetchAssistantCatMaster({ commit }) {
    const master = await assistantCatClient.getAssistantCatMaster();
    commit("setAssistantCatMaster", master);
    return master;
  },
  async fetchAssistantCatList({ commit }) {
    const cats = await assistantCatClient.listAssistantCat();
    commit("setAssistantCats", cats);
    return cats;
  },
  async updateAssistantCatMaster({ commit }, input: UpdateAssistantCatMasterInput) {
    const updatedMaster = await assistantCatClient.updateAssistantCatMaster(input);
    commit("setAssistantCatMaster", updatedMaster);
    return updatedMaster;
  },
  async updateCatNickname({ commit }, input: UpdateAssistantCatInput) {
    const updatedCat = await assistantCatClient.updateAssistantCat(input);
    commit("updateAssistantCatInList", updatedCat);
    return updatedCat;
  },
  async createAssistantCatMasterOnSkip({ commit }) {
    const createMaster = await assistantCatClient.createAssistantCatMasterOnSkip();
    commit("setAssistantCatMaster", createMaster);
    return createMaster;
  },
  async fetchDryModeExtraFish({ commit }) {
    const extraFish = await assistantCatClient.getDryModeExtraFish();
    commit("setDryModeExtraFish", extraFish);
    return extraFish;
  },
};

const state: AssistantCatState = {
  selectedType: null,
  serializedResult: null,
  assistantCatMaster: null,
  assistantCats: [],
  dryModeExtraFish: 0,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
